import { startOfDay } from "date-fns";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { DateSelectionMode, OverviewPeriod } from "../../components/DatePeriodSelector/types";
import { getDaysAgoDate } from "../utils/date/dayAgo";
import {
  OverviewState,
  FetchOverviewDomainsPayload,
  OverviewDomainsAggRow,
  FetchSourceDomainsPagePayload,
  FetchSourceDomainsChartPayload,
  FetchSourceDetailLinksPayload,
  SourceDetailLinkRow,
} from "./types";

const SOURCE_PAGE_SIZE = 20;

const initialState: OverviewState = {
  selectedModelId: null,
  dateFilter: {
    type: DateSelectionMode.Period,
    period: OverviewPeriod.SevenDays,
    startTs: startOfDay(getDaysAgoDate(7)).getTime(),
  },
  overviewDomains: {
    loading: "idle",
    data: null,
    error: null,
  },
  sourceDomainsPage: {
    loading: "idle",
    data: null,
    total: 0,
    page: 1,
    pageSize: SOURCE_PAGE_SIZE,
    error: null,
  },
  sourceDomainsChartData: null,
  sourceDomainsChartDataLoading: false,
  sourceDetailLinks: {
    loading: "idle",
    data: null,
    error: null,
  },
};

const overviewSlice = createSlice({
  name: "overview",
  initialState,
  reducers: {
    setSelectedModelId: (state, action: PayloadAction<number | null>) => {
      state.selectedModelId = action.payload;
    },
    setOverviewDateFilter: (state, action: PayloadAction<OverviewState["dateFilter"]>) => {
      state.dateFilter = action.payload;
    },
    fetchOverviewDomains: (state, action: PayloadAction<FetchOverviewDomainsPayload>) => {
      void action;
      state.overviewDomains.loading = "loading";
      state.overviewDomains.data = null;
      state.overviewDomains.error = null;
    },
    fetchOverviewDomainsSuccess: (state, action: PayloadAction<OverviewDomainsAggRow[]>) => {
      state.overviewDomains.loading = "success";
      state.overviewDomains.data = action.payload;
    },
    fetchOverviewDomainsFailure: (state, action: PayloadAction<string | undefined>) => {
      state.overviewDomains.loading = "error";
      state.overviewDomains.error = action.payload ?? null;
    },
    fetchSourceDomainsPage: (state, action: PayloadAction<FetchSourceDomainsPagePayload>) => {
      void action;
      state.sourceDomainsPage.loading = "loading";
      state.sourceDomainsPage.error = null;
    },
    fetchSourceDomainsPageSuccess: (
      state,
      action: PayloadAction<{ data: OverviewDomainsAggRow[]; total: number; page: number; pageSize: number }>
    ) => {
      state.sourceDomainsPage.loading = "success";
      state.sourceDomainsPage.data = action.payload.data;
      state.sourceDomainsPage.total = action.payload.total;
      state.sourceDomainsPage.page = action.payload.page;
      state.sourceDomainsPage.pageSize = action.payload.pageSize;
    },
    fetchSourceDomainsPageFailure: (state, action: PayloadAction<string | undefined>) => {
      state.sourceDomainsPage.loading = "error";
      state.sourceDomainsPage.error = action.payload ?? null;
    },
    setSourceDomainsPage: (
      state,
      action: PayloadAction<{ page: number }>
    ) => {
      state.sourceDomainsPage.page = action.payload.page;
    },
    fetchSourceDomainsChartData: (state, action: PayloadAction<FetchSourceDomainsChartPayload>) => {
      void action;
      state.sourceDomainsChartDataLoading = true;
      state.sourceDomainsChartData = null;
    },
    fetchSourceDomainsChartDataSuccess: (state, action: PayloadAction<OverviewDomainsAggRow[]>) => {
      state.sourceDomainsChartDataLoading = false;
      state.sourceDomainsChartData = action.payload;
    },
    fetchSourceDomainsChartDataFailure: (state, action: PayloadAction<string | undefined>) => {
      void action;
      state.sourceDomainsChartDataLoading = false;
      state.sourceDomainsChartData = null;
    },
    setSourceDomainsChartData: (state, action: PayloadAction<OverviewDomainsAggRow[] | null>) => {
      state.sourceDomainsChartData = action.payload;
    },
    fetchSourceDetailLinks: (state, action: PayloadAction<FetchSourceDetailLinksPayload>) => {
      void action;
      state.sourceDetailLinks.loading = "loading";
      state.sourceDetailLinks.data = null;
      state.sourceDetailLinks.error = null;
    },
    fetchSourceDetailLinksSuccess: (state, action: PayloadAction<SourceDetailLinkRow[]>) => {
      state.sourceDetailLinks.loading = "success";
      state.sourceDetailLinks.data = action.payload;
    },
    fetchSourceDetailLinksFailure: (state, action: PayloadAction<string | undefined>) => {
      state.sourceDetailLinks.loading = "error";
      state.sourceDetailLinks.error = action.payload ?? null;
    },
  },
});

export const {
  setSelectedModelId,
  setOverviewDateFilter,
  fetchOverviewDomains,
  fetchOverviewDomainsSuccess,
  fetchOverviewDomainsFailure,
  fetchSourceDomainsPage,
  fetchSourceDomainsPageSuccess,
  fetchSourceDomainsPageFailure,
  setSourceDomainsPage,
  fetchSourceDomainsChartData,
  fetchSourceDomainsChartDataSuccess,
  fetchSourceDomainsChartDataFailure,
  setSourceDomainsChartData,
  fetchSourceDetailLinks,
  fetchSourceDetailLinksSuccess,
  fetchSourceDetailLinksFailure,
} = overviewSlice.actions;

export default overviewSlice.reducer;
